import { useCallback } from 'react';
import {
  ResourceIdentifier,
  ResourceType,
} from 'api/resource-management/types';
import { GroupBy } from '../types';
import {
  resourceIdForProject,
  resourceIdForProjectAsLeaf,
  resourceIdForTeamAsLeaf,
  resourceIdForUserAsLeaf,
} from '../utils/id';
import { CommonManagerProps } from './types';

interface Result {
  setTotalAllocationForProject: (
    projectId: number,
    totalMinutes: number,
  ) => void;
  setTotalAllocationForResource: (
    projectId: number,
    resourceIdentifier: ResourceIdentifier,
    totalMinutes: number,
  ) => void;
  updateTotalAllocation: (
    projectId: number,
    resourceIdentifier: ResourceIdentifier,
    differenceInTotalMinutes: number,
  ) => void;
}
export const useTotalAllocationInUIManager = (
  props: CommonManagerProps,
): Result => {
  const { groupBy, schedulerRef } = props;

  const setTotalAllocationForProject = useCallback(
    (projectId: number, totalMinutes: number) => {
      if (groupBy === GroupBy.Projects) {
        const projectLevelResource =
          schedulerRef.current.instance.resourceStore.getById(
            resourceIdForProject(projectId),
          );

        if (projectLevelResource != null) {
          projectLevelResource.set('totalAllocation', totalMinutes);
        }
      }
    },
    [groupBy, schedulerRef],
  );
  const getResourceFromIdentifier = useCallback(
    (projectId: number, resourceIdentifier: ResourceIdentifier) => {
      if (groupBy === GroupBy.Projects) {
        if (resourceIdentifier.type === ResourceType.User) {
          return schedulerRef.current.instance.resourceStore.getById(
            resourceIdForUserAsLeaf({
              projectId,
              userId: resourceIdentifier.userId,
              roleId: resourceIdentifier.roleId,
              referenceTeamId: resourceIdentifier.referenceTeamId,
            }),
          );
        }
        if (resourceIdentifier.type === ResourceType.Team) {
          return schedulerRef.current.instance.resourceStore.getById(
            resourceIdForTeamAsLeaf(projectId, resourceIdentifier.teamId),
          );
        }
      } else if (groupBy === GroupBy.Users) {
        if (resourceIdentifier.type === ResourceType.User) {
          return schedulerRef.current.instance.resourceStore.getById(
            resourceIdForProjectAsLeaf({
              projectId,
              userId: resourceIdentifier.userId,
              roleId: resourceIdentifier.roleId,
              referenceTeamId: resourceIdentifier.referenceTeamId,
            }),
          );
        }
        if (resourceIdentifier.type === ResourceType.Team) {
          return schedulerRef.current.instance.resourceStore.getById(
            resourceIdForTeamAsLeaf(projectId, resourceIdentifier.teamId),
          );
        }
      }

      return undefined;
    },
    [groupBy, schedulerRef],
  );

  const setTotalAllocationForResource = useCallback(
    (
      projectId: number,
      resourceIdentifier: ResourceIdentifier,
      totalMinutes: number,
    ) => {
      const resource = getResourceFromIdentifier(projectId, resourceIdentifier);

      if (resource != null) {
        resource.set('totalAllocation', totalMinutes);
      }
    },
    [getResourceFromIdentifier],
  );
  const updateTotalAllocation = useCallback(
    (
      projectId: number,
      resourceIdentifier: ResourceIdentifier,
      differenceInTotalMinutes: number,
    ) => {
      if (groupBy === GroupBy.Projects) {
        const projectLevelResource =
          schedulerRef.current.instance.resourceStore.getById(
            resourceIdForProject(projectId),
          );

        if (projectLevelResource != null) {
          setTotalAllocationForProject(
            projectId,
            projectLevelResource.get('totalAllocation') +
              differenceInTotalMinutes,
          );
        }
      }

      const resource = getResourceFromIdentifier(projectId, resourceIdentifier);

      if (resource != null) {
        setTotalAllocationForResource(
          projectId,
          resourceIdentifier,
          resource.get('totalAllocation') + differenceInTotalMinutes,
        );
      }
    },
    [
      getResourceFromIdentifier,
      groupBy,
      schedulerRef,
      setTotalAllocationForProject,
      setTotalAllocationForResource,
    ],
  );

  return {
    setTotalAllocationForProject,
    setTotalAllocationForResource,
    updateTotalAllocation,
  };
};
